import React, { useState } from 'react';

interface RequestMoreStoresProps {
  onSubmit: (justification: string, requestedCount: number) => void;
  onCancel: () => void;
  currentStoreCount: number;
  appName: string;
}

const RequestMoreStores: React.FC<RequestMoreStoresProps> = ({
  onSubmit,
  onCancel,
  currentStoreCount,
  appName
}) => {
  const [requestedCount, setRequestedCount] = useState<number>(2);
  const [justification, setJustification] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (requestedCount <= currentStoreCount) {
      newErrors.requestedCount = `Requested count must be greater than current count (${currentStoreCount})`;
    }

    if (requestedCount > 50) {
      newErrors.requestedCount = 'Maximum of 50 stores can be requested at once';
    }

    if (!justification.trim()) {
      newErrors.justification = 'Business justification is required';
    }

    if (justification.trim().length < 50) {
      newErrors.justification = 'Please provide a detailed justification (minimum 50 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit(justification.trim(), requestedCount);
    }
  };

  const handleJustificationChange = (value: string) => {
    setJustification(value);
    if (errors.justification) {
      setErrors(prev => ({ ...prev, justification: '' }));
    }
  };

  const handleCountChange = (value: number) => {
    setRequestedCount(value);
    if (errors.requestedCount) {
      setErrors(prev => ({ ...prev, requestedCount: '' }));
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container request-stores-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h3>Request Additional Stores</h3>
            <button 
              type="button" 
              className="modal-close"
              onClick={onCancel}
            >
              ×
            </button>
          </div>
          
          <div className="modal-body">
            <div className="request-info">
              <div className="current-status">
                <h4>Current Status</h4>
                <div className="status-grid">
                  <div className="status-item">
                    <label>App Name:</label>
                    <span>{appName}</span>
                  </div>
                  <div className="status-item">
                    <label>App Type:</label>
                    <span>Single Store</span>
                  </div>
                  <div className="status-item">
                    <label>Current Stores:</label>
                    <span>{currentStoreCount}</span>
                  </div>
                  <div className="status-item">
                    <label>Store Limit:</label>
                    <span>1 (Single Store)</span>
                  </div>
                </div>
              </div>

              <div className="upgrade-info">
                <div className="info-box warning">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <div>
                    <p><strong>App Type Upgrade Required</strong></p>
                    <p>To add more stores, your app type will need to be upgraded from "Single Store" to "Multi-Store" or "Single Store Multi-Location". This request will be reviewed by an administrator.</p>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h4>Request Details</h4>
                
                <div className="form-field">
                  <label>Requested Number of Stores *</label>
                  <div className="number-input-group">
                    <input
                      type="number"
                      min={currentStoreCount + 1}
                      max={50}
                      value={requestedCount}
                      onChange={(e) => handleCountChange(parseInt(e.target.value) || 1)}
                      className={errors.requestedCount ? 'error' : ''}
                    />
                    <span className="input-help">stores total</span>
                  </div>
                  {errors.requestedCount && <span className="error-text">{errors.requestedCount}</span>}
                  <small className="form-help-text">
                    This is the total number of stores you want (including existing ones). 
                    You currently have {currentStoreCount} store{currentStoreCount !== 1 ? 's' : ''}.
                  </small>
                </div>

                <div className="form-field">
                  <label>Business Justification *</label>
                  <textarea
                    value={justification}
                    onChange={(e) => handleJustificationChange(e.target.value)}
                    placeholder="Please explain why you need additional stores. Include details about your business expansion plans, target locations, expected revenue, etc."
                    rows={6}
                    className={errors.justification ? 'error' : ''}
                  />
                  <div className="char-count">
                    {justification.length} / 1000 characters
                  </div>
                  {errors.justification && <span className="error-text">{errors.justification}</span>}
                  <small className="form-help-text">
                    Provide detailed information about your business needs, expansion plans, and how additional stores will benefit your operations.
                  </small>
                </div>
              </div>

              <div className="review-process-info">
                <h4>Review Process</h4>
                <div className="process-steps">
                  <div className="process-step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h5>Request Submission</h5>
                      <p>Your request will be submitted to our admin team for review.</p>
                    </div>
                  </div>
                  <div className="process-step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h5>Admin Review</h5>
                      <p>Our team will evaluate your business justification and requirements.</p>
                    </div>
                  </div>
                  <div className="process-step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h5>App Type Upgrade</h5>
                      <p>If approved, your app type will be upgraded to support multiple stores.</p>
                    </div>
                  </div>
                  <div className="process-step">
                    <div className="step-number">4</div>
                    <div className="step-content">
                      <h5>Add New Stores</h5>
                      <p>You'll be able to add additional stores to your app.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pricing-note">
                <div className="info-box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                  <div>
                    <p><strong>Note:</strong> Additional stores may incur extra fees. Pricing details will be provided during the review process.</p>
                  </div>
                </div>
              </div>
            </form>
          </div>
          
          <div className="modal-footer">
            <button
              type="button"
              className="submit-btn"
              onClick={handleSubmit}
            >
              Submit Request
            </button>
            <button
              type="button"
              className="cancel-btn"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestMoreStores;